import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Sparkles,
  LogOut,
  User as UserIcon,
  MessageSquarePlus,
  Menu,
  Users,
  HelpCircle,
  CheckCircle2,
  Info,
} from "lucide-react";
import { useAuth, goToLogin, signInLabel } from "@/contexts/AuthContext";
import ContactUsModal from "@/components/ContactUsModal";
import oiiisterLogo from "@/assets/oiiister-logo.png";
import oiiisterWordmark from "@/assets/oiiister-wordmark.png";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "About Oiiister", to: "/about", Icon: Info },
  { label: "Professional Directory", to: "/", Icon: Users },
  { label: "Ask a Question", to: "/problems", Icon: HelpCircle },
  { label: "Answered Questions", to: "/closed-problems", Icon: CheckCircle2 },
];

function NavItems({ pathname, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ label, to, Icon }) => {
        const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-medium transition-colors ${
              active
                ? "bg-[#111827] text-white"
                : "text-[#4B5563] hover:text-[#111827] hover:bg-white"
            }`}
          >
            <Icon size={16} strokeWidth={2.2} />
            <span>{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function UserSection({ user, logout, onOpenContact, pathname, onNavigate, navigate }) {
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        data-testid="sidebar-contact"
        onClick={() => {
          onNavigate?.();
          onOpenContact();
        }}
        className="flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-medium text-[#4B5563] hover:text-[#3FFFB6] hover:bg-white transition-colors"
      >
        <MessageSquarePlus size={16} />
        Report / Request
      </button>

      <div className="pt-2 mt-1 border-t border-[#F3F4F6]">
        {user ? (
          !user.profile_complete ? (
            <button
              type="button"
              data-testid="sidebar-finish-profile"
              onClick={() => {
                onNavigate?.();
                navigate("/my/profile");
              }}
              className="w-full inline-flex items-center gap-2 justify-center h-11 mt-2 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold transition-colors"
            >
              <Sparkles size={14} />
              Finish profile
            </button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu"
                className="w-full mt-2 flex items-center gap-2.5 rounded-2xl px-2 py-2 ring-1 ring-[#F3F4F6] bg-white hover:ring-[#B8FFDD] transition-colors text-left"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#4B5563]">
                    <UserIcon size={16} />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#111827] truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-[#9CA3AF] truncate">
                    {user.email}
                  </p>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-52">
                <DropdownMenuItem
                  data-testid="menu-profile"
                  onClick={() => {
                    onNavigate?.();
                    navigate("/my/profile");
                  }}
                >
                  <UserIcon size={14} className="mr-2" />
                  My profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => {
                    onNavigate?.();
                    await logout();
                    navigate("/");
                  }}
                >
                  <LogOut size={14} className="mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        ) : (
          <button
            data-testid="sidebar-signin"
            onClick={() => {
              onNavigate?.();
              goToLogin(pathname);
            }}
            className="w-full h-11 mt-2 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold transition-colors"
          >
            {signInLabel()}
          </button>
        )}
      </div>
    </div>
  );
}

function SidebarBody({ pathname, user, logout, onOpenContact, onNavigate, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <Link
        to="/"
        onClick={onNavigate}
        data-testid="brand-link"
        className="flex items-center gap-2 group px-2 mb-8"
      >
        <img
          src={oiiisterLogo}
          alt="Oiiister"
          className="w-10 h-10 rounded-2xl transition-transform duration-300 group-hover:rotate-[-6deg]"
        />
        <img
          src={oiiisterWordmark}
          alt="Oiiister"
          className="h-5 w-auto"
        />
      </Link>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <NavItems pathname={pathname} onNavigate={onNavigate} />
      </div>

      <div className="mt-6">
        <UserSection
          user={user}
          logout={logout}
          onOpenContact={onOpenContact}
          pathname={pathname}
          onNavigate={onNavigate}
          navigate={navigate}
        />
      </div>
    </div>
  );
}

export default function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [contactOpen, setContactOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const openContact = () => setContactOpen(true);
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar — fixed to viewport, scrolls independently */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex fixed top-0 left-0 bottom-0 w-64 bg-[#FAFAFA]/85 backdrop-blur-md border-r border-[#F3F4F6] p-5 z-40"
      >
        <SidebarBody
          pathname={pathname}
          user={user}
          logout={logout}
          onOpenContact={openContact}
          navigate={navigate}
        />
      </aside>

      {/* Mobile top bar with hamburger */}
      <header
        data-testid="mobile-topbar"
        className="md:hidden sticky top-0 z-40 bg-[#FAFAFA]/85 backdrop-blur-md border-b border-[#F3F4F6]"
      >
        <div className="h-14 px-4 flex items-center justify-between">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              data-testid="mobile-menu-trigger"
              className="w-10 h-10 rounded-xl inline-flex items-center justify-center text-[#111827] hover:bg-white transition-colors"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-5">
              <SheetHeader className="sr-only">
                <SheetTitle>Menu</SheetTitle>
                <SheetDescription>
                  Navigate Oiiister and access your account.
                </SheetDescription>
              </SheetHeader>
              <SidebarBody
                pathname={pathname}
                user={user}
                logout={logout}
                onOpenContact={openContact}
                onNavigate={closeMobile}
                navigate={navigate}
              />
            </SheetContent>
          </Sheet>

          <Link
            to="/"
            data-testid="mobile-brand-link"
            className="flex items-center gap-2"
          >
            <img src={oiiisterLogo} alt="Oiiister" className="w-8 h-8 rounded-xl" />
            <img src={oiiisterWordmark} alt="Oiiister" className="h-4 w-auto" />
          </Link>

          <div className="w-10" />
        </div>
      </header>

      {/* Main content — offset by sidebar on md+ */}
      <main className="md:pl-64">
        <div className="min-h-[calc(100vh-3.5rem)] md:min-h-screen flex flex-col">
          <div className="flex-1">
            <Outlet />
          </div>

          <footer className="mt-24 border-t border-[#F3F4F6] py-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-[#9CA3AF] font-body">
                © {new Date().getFullYear()} Oiiister · A directory of people who
                solve real problems with AI.
              </p>
              <div className="flex items-center gap-5">
                <Link
                  to="/about"
                  data-testid="footer-about"
                  className="text-sm font-medium text-[#4B5563] hover:text-[#3FFFB6] transition-colors"
                >
                  About Oiiister
                </Link>
                <button
                  type="button"
                  data-testid="footer-contact"
                  onClick={() => setContactOpen(true)}
                  className="text-sm font-medium text-[#4B5563] hover:text-[#3FFFB6] transition-colors inline-flex items-center gap-1.5"
                >
                  <MessageSquarePlus size={14} />
                  Report / Request
                </button>
              </div>
            </div>
          </footer>
        </div>
      </main>

      <ContactUsModal open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
